import { Controller } from "@hotwired/stimulus";

const controllers = new Set();
let clickHandler = null;
let popStateHandler = null;

export default class ModalHistoryController extends Controller {
  static values = {
    linkSelector: {
      type: String,
      default: "[data-modal-history-link]"
    },
    pagePattern: String
  };

  connect() {
    this.pageRegex = this.createPageRegex();
    this.handleModalHide = this.handleModalHide.bind(this);

    controllers.add(this);
    this.setupGlobalListeners();
    this.element.addEventListener("hide.bs.modal", this.handleModalHide);
  }

  disconnect() {
    this.element.removeEventListener("hide.bs.modal", this.handleModalHide);
    controllers.delete(this);

    if (controllers.size === 0) {
      this.removeGlobalListeners();
    }
  }

  setupGlobalListeners() {
    if (clickHandler) return;

    clickHandler = (event) => {
      for (const controller of controllers) {
        controller.handleLinkClick(event);
      }
    };

    popStateHandler = (event) => {
      for (const controller of controllers) {
        controller.handlePopState(event);
      }
    };

    document.addEventListener("click", clickHandler);
    globalThis.addEventListener("popstate", popStateHandler);
  }

  removeGlobalListeners() {
    document.removeEventListener("click", clickHandler);
    globalThis.removeEventListener("popstate", popStateHandler);
    clickHandler = null;
    popStateHandler = null;
  }

  handleLinkClick(event) {
    const link = event.target.closest(this.linkSelectorValue);

    if (!this.isHistoryLink(link)) return;

    const currentUrl = this.getCurrentUrl();
    const modalUrl = this.getLinkUrl(link);

    if (currentUrl === modalUrl) return;

    this.pushHistory(modalUrl, currentUrl);
  }

  handlePopState(event) {
    if (!event.state?.modalHistory || !this.isModalPage()) return;

    globalThis.location.reload();
  }

  handleModalHide() {
    const historyState = globalThis.history.state;

    if (!historyState?.modalHistory || !historyState.modalFrom) return;

    this.restorePreviousUrl(historyState);
  }

  isHistoryLink(link) {
    return Boolean(
      link &&
      link.dataset.turboFrame !== "_top" &&
      link.dataset.modalHistoryLink
    );
  }

  getCurrentUrl() {
    return `${globalThis.location.pathname}${globalThis.location.search}`;
  }

  getLinkUrl(link) {
    const url = new URL(link.href, globalThis.location.origin);
    return `${url.pathname}${url.search}`;
  }

  pushHistory(modalUrl, currentUrl) {
    globalThis.history.pushState(
      {
        ...globalThis.history.state,
        modalHistory: true,
        modalFrom: currentUrl
      },
      "",
      modalUrl
    );
  }

  restorePreviousUrl(historyState) {
    globalThis.history.replaceState(
      { ...historyState, modalHistory: false },
      "",
      historyState.modalFrom
    );
  }

  createPageRegex() {
    return this.hasPagePatternValue
      ? new RegExp(this.pagePatternValue)
      : null;
  }

  isModalPage() {
    return !this.pageRegex ||
      this.pageRegex.test(globalThis.location.pathname);
  }
}
